import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { FaSpinner, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
// Import styles for results history
import styles from './ListingScoreResultHistory.module.css';
import ResultsSection from '../components/ResultsSection';
import ActionPlanSection from '../components/ActionPlanSection';
import ComponentAnalysisSection from '../components/ComponentAnalysisSection';
import listingScoreStyles from '../ListingScore.module.css';
import AuthContext from '../../../context/AuthContext';

// Constants
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PRODUCTS_PER_PAGE = 100;

// Helper function to convert percentage to points (0-100 -> 0-10)
const convertToPoints = (percentage) => {
    if (percentage === null || percentage === undefined) return 0;
    return (percentage / 10).toFixed(1);
};

// Get score color based on score value
const getScoreColor = (score) => {
    if (score >= 80) return '#4CAF50'; // Green
    if (score >= 60) return '#FFA726'; // Orange
    return '#F44336'; // Red
};

// Result Card Component
const ResultCard = ({ result, index, onViewScore, onUpgrade }) => {
    const hasScore = result.has_score;
    const score = result.score || 0;
    const title = result.product_info?.title;
    const mainImage = result.product_info?.main_image;
    const criticalGaps = Array.isArray(result.analysis?.critical_gaps)
        ? result.analysis.critical_gaps
        : [];

    return (
        <div className={styles.resultCard}>
            {hasScore && (
                <div className={styles.cardOverlay}>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewScore(result.asin, result);
                        }}
                        className={styles.cardOverlayButton}
                        aria-label="View result"
                    >
                        View
                    </button>
                </div>
            )}
            <div className={styles.cardContent}>
                <div className={styles.cardTopSection}>
                    {mainImage && (
                        <div className={styles.cardImageWrapper}>
                            <img
                                src={mainImage}
                                alt={title || result.asin || 'Product image'}
                                className={styles.cardImage}
                            />
                        </div>
                    )}
                    <div className={styles.cardInfoSection}>
                        {title && (
                            <div className={styles.cardTitle}>
                                {title}
                            </div>
                        )}
                        <div className={styles.cardAsinBadge}>
                            <span className={styles.cardAsinText}>{result.asin || 'N/A'}</span>
                            {result.asin && (
                                <a
                                    href={`https://amazon.in/dp/${result.asin}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.cardAsinLink}
                                    aria-label={`View product ${result.asin} on Amazon`}
                                >
                                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M11.1521 0.199252H8.75283C8.43127 0.199252 8.1696 0.460925 8.1696 0.782488C8.1696 1.10405 8.43127 1.36572 8.75283 1.36572H10.2077L5.71042 5.86297C5.48194 6.09145 5.48194 6.45923 5.71042 6.6877C5.82368 6.80097 5.97339 6.85954 6.12182 6.85954C6.27024 6.85954 6.41995 6.80291 6.53322 6.6877L11.0305 2.19045V3.49037C11.0305 3.81193 11.2921 4.07361 11.6137 4.07361C11.9353 4.07361 12.1969 3.81193 12.1969 3.49037V1.24591C12.1969 0.66919 11.727 0.199219 11.1502 0.199219L11.1521 0.199252Z" fill="#5488C7" stroke="#F7FCFF" strokeWidth="0.4"/>
                                        <path d="M11.615 5.3052C11.2934 5.3052 11.0317 5.56687 11.0317 5.88844V9.26087C11.0317 10.236 10.2382 11.0307 9.26185 11.0307H3.13654C2.16144 11.0307 1.36667 10.2373 1.36667 9.26087V3.13556C1.36667 2.16046 2.16015 1.36569 3.13654 1.36569H6.50898C6.83054 1.36569 7.09221 1.10402 7.09221 0.782455C7.09221 0.460892 6.83054 0.199219 6.50898 0.199219H3.13654C1.51833 0.199219 0.200195 1.5154 0.200195 3.13556V9.26287C0.200195 10.8811 1.51638 12.1992 3.13654 12.1992H9.26385C10.8821 12.1992 12.2002 10.883 12.2002 9.26287V5.89044C12.2002 5.56887 11.9385 5.3072 11.617 5.3072L11.615 5.3052Z" fill="#5488C7" stroke="#F7FCFF" strokeWidth="0.4"/>
                                    </svg>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
                
                <div className={styles.cardBottomSection}>
                    {hasScore && (
                        <div className={styles.cardScoreContainer}>
                            <div className={styles.cardScoreBar}></div>
                            <div className={styles.cardScoreContent}>
                                <div className={styles.cardScoreLabel}>Listing Score</div>
                                <div className={styles.cardScoreValue}>
                                    {convertToPoints(score)}
                                    <span className={styles.cardScoreMax}>/10</span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {criticalGaps.length > 0 && (
                        <div className={styles.cardGaps}>
                            {criticalGaps.map((gap, i) => (
                                <span key={i} className={styles.gapTag}>
                                    {gap}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            
            {!hasScore && (
                <div className={styles.cardActions}>
                    <span className={styles.noScore}>No Score Available</span>
                </div>
            )}
        </div>
    );
};

// Pagination Component (reused from Catalogue)
const Pagination = React.memo(({ currentPage, totalPages, onPageChange }) => {
    const handlePrevPage = useCallback(() => onPageChange(currentPage - 1), [currentPage, onPageChange]);
    const handleNextPage = useCallback(() => onPageChange(currentPage + 1), [currentPage, onPageChange]);
    
    if (totalPages <= 1) return null;

    return (
        <div className={styles.pagination}>
            <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={styles.paginationButton}
                aria-label="Previous page"
            >
                <FaChevronLeft />
            </button>
            
            <span className={styles.pageInfo}>
                Page {currentPage} of {totalPages}
            </span>
            
            <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={styles.paginationButton}
                aria-label="Next page"
            >
                <FaChevronRight />
            </button>
        </div>
    );
});

// Groups flat results by task_id. Returns array of { task_id, completed_at, results } sorted by completed_at desc.
function groupResultsByBatch(flatResults) {
    const byTask = {};
    for (const r of flatResults) {
        const tid = r.task_id || 'unknown';
        if (!byTask[tid]) byTask[tid] = { task_id: tid, completed_at: r.completed_at, results: [] };
        byTask[tid].results.push(r);
        if (r.completed_at && (!byTask[tid].completed_at || new Date(r.completed_at) > new Date(byTask[tid].completed_at)))
            byTask[tid].completed_at = r.completed_at;
    }
    return Object.values(byTask).sort((a, b) => {
        const da = a.completed_at ? new Date(a.completed_at) : new Date(0);
        const db = b.completed_at ? new Date(b.completed_at) : new Date(0);
        return db - da;
    });
}

function formatBatchDate(d) {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatBatchDateTime(d) {
    if (!d) return '';
    const date = new Date(d);
    const dateStr = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    const timeStr = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return `${dateStr} | ${timeStr}`;
}

// How many thumbnails to show: 2→2, 3→3, 4→4, 5→4, 6→6, 7→6, 8+→8. Grid cols: 2→2, 3→3, 4→2, 6→3, 8→4.
function getThumbnailDisplay(count) {
    const displayCount = count <= 4 ? count : count === 5 ? 4 : count <= 7 ? 6 : 8;
    const gridCols = displayCount <= 1 ? 1 : displayCount <= 3 ? displayCount : displayCount === 4 ? 2 : displayCount === 6 ? 3 : 4;
    return { displayCount, gridCols };
}

// Batch card: stacked cards, image grid, ASIN count, ASIN range, date. Click opens that batch's ASIN cards.
const BatchCard = React.memo(({ batch, onClick }) => {
    const count = batch.results.length;
    const asins = batch.results.map((r) => r.asin).filter(Boolean);
    const asinRange = asins.length >= 2
        ? `${asins[0]} - ${asins[asins.length - 1]}`
        : asins[0] || '—';
    const thumbnails = batch.results.map((r) => r.product_info?.main_image);
    const { displayCount, gridCols } = getThumbnailDisplay(count);
    const thumbsToShow = thumbnails.slice(0, displayCount);

    return (
        <div className={styles.batchCardStack} onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClick()}>
            <div className={`${styles.batchCardStackBehind} ${styles.batchCardStackBehindFirst}`} />
            <div className={`${styles.batchCardStackBehind} ${styles.batchCardStackBehindSecond}`} />
            <div className={styles.batchCardMain}>
                <div
                    className={styles.batchCardThumbGrid}
                    style={{
                        gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                        gridTemplateRows: `repeat(${Math.ceil(displayCount / gridCols)}, 1fr)`,
                    }}
                >
                    {thumbsToShow.map((src, i) => (
                        <div key={i} className={styles.batchCardThumb}>
                            {src && <img src={src} alt="" />}
                        </div>
                    ))}
                </div>
                <div className={styles.batchCardContent}>
                    <div className={styles.batchCardAsinCount}>
                        {count} ASIN{count !== 1 ? 's' : ''}
                    </div>
                    <div className={styles.batchCardAsinTag}>
                        <span className={styles.batchCardAsinTagText}>{asinRange}</span>
                    </div>
                    <div className={styles.batchCardDate}>
                        {formatBatchDateTime(batch.completed_at)}
                    </div>
                </div>
            </div>
        </div>
    );
});

// Results Grid Component (card-based layout)
const ResultsGrid = React.memo(({ results, onViewScore, currentPage, productsPerPage, onUpgrade }) => {
    const currentResults = useMemo(() => {
        const startIndex = (currentPage - 1) * productsPerPage;
        const endIndex = startIndex + productsPerPage;
        return results.slice(startIndex, endIndex);
    }, [results, currentPage, productsPerPage]);

    const startIndex = useMemo(() => (currentPage - 1) * productsPerPage, [currentPage, productsPerPage]);

    return (
        <div className={styles.resultsGrid}>
            {currentResults.map((result, index) => (
                <ResultCard
                    key={`${result.asin}-${startIndex + index}`}
                    result={result}
                    index={startIndex + index}
                    onViewScore={onViewScore}
                    onUpgrade={onUpgrade}
                />
            ))}
        </div>
    );
});

// Main Component
const ListingScoreResultHistory = () => {
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedBatchId, setSelectedBatchId] = useState(null);
    const [selectedScoreData, setSelectedScoreData] = useState(null);
    const [showDetailedView, setShowDetailedView] = useState(false);

    // Fetch listing score history
    const fetchListingScoreHistory = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setCurrentPage(1);
            setSelectedBatchId(null);
            const token = authCtx.token;
            if (!token) {
                throw new Error('No authentication token found. Please log in again.');
            }
            
            const response = await fetch(`${API_BASE_URL}/listing_score/all-scores`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch results: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.success && data.results) {
                // Sort results by completed_at (most recent first)
                const sortedResults = [...data.results].sort((a, b) => {
                    const dateA = a.completed_at ? new Date(a.completed_at) : new Date(0);
                    const dateB = b.completed_at ? new Date(b.completed_at) : new Date(0);
                    return dateB - dateA; // Descending order (most recent first)
                });
                setResults(sortedResults);
            } else {
                setResults([]);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [authCtx.token]);

    useEffect(() => {
        if (authCtx.isLoggedIn) {
            fetchListingScoreHistory();
        } else {
            setLoading(false);
            setError('Please log in to view results');
        }
    }, [authCtx.isLoggedIn, fetchListingScoreHistory]);

    const handleUpgradeListing = useCallback((asin) => {
        navigate('/dashboard/generate/single', {
            state: {
                isNewListing: false,
                initialAsin: asin || ''
            }
        });
    }, [navigate]);

    const batches = useMemo(() => groupResultsByBatch(results), [results]);
    const selectedBatch = useMemo(() => batches.find(b => b.task_id === selectedBatchId), [batches, selectedBatchId]);
    const appliedBatchFromStateRef = useRef(false);

    // Open batch from Manage page (location.state.selectedBatchId) once on load
    useEffect(() => {
        const fromState = location.state?.selectedBatchId;
        if (fromState && !appliedBatchFromStateRef.current && batches.some((b) => b.task_id === fromState)) {
            appliedBatchFromStateRef.current = true;
            setSelectedBatchId(fromState);
            setCurrentPage(1);
        }
    }, [batches, location.state?.selectedBatchId]);
    const batchResults = selectedBatch ? selectedBatch.results : [];
    const hasBatches = batches.length > 0;
    const batchTotalPages = Math.ceil(batchResults.length / PRODUCTS_PER_PAGE);
    const handleBackToBatches = useCallback(() => {
        setSelectedBatchId(null);
        setCurrentPage(1);
    }, []);
    const handleBatchPageChange = useCallback((newPage) => {
        setCurrentPage(Math.max(1, Math.min(newPage, batchTotalPages)));
    }, [batchTotalPages]);

    const handleViewScore = useCallback((asin, scoreData) => {
        if (!scoreData || !scoreData.has_score) return;

        // Transform the score data to match the format expected by components
        const analysis = scoreData.analysis || {};
        const productInfo = scoreData.product_info || {};

        const transformedData = {
            asin: asin,
            title: productInfo.title || 'Product Title',
            main_image: productInfo.main_image || '',
            b_plus_images: productInfo.b_plus_images || productInfo.all_images || [],
            description: productInfo.description || '',
            bullet_points: productInfo.bullet_points || [],
            specifications: productInfo.specifications || [],
            a_plus_content: productInfo.a_plus_content || false,
            analysis: {
                total_score: scoreData.total_score || 0,
                percentage_score: scoreData.score || 0,
                overall_analysis: analysis.overall_analysis || '',
                component_scores: analysis.component_scores || {},
                competitive_advantages: analysis.competitive_advantages || [],
                improvement_areas: analysis.improvement_areas || [],
                critical_gaps: analysis.critical_gaps || [],
                recommendations: analysis.recommendations || [],
            },
            product_info: productInfo,
        };

        setSelectedScoreData(transformedData);
        setShowDetailedView(true);
    }, []);

    const handleCloseDetailedView = useCallback(() => {
        setShowDetailedView(false);
        setSelectedScoreData(null);
    }, []);

    // Loading state
    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.detailedViewHeader}>
                    <button 
                        className={styles.backButton}
                        onClick={() => navigate(-1)}
                        aria-label="Back"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 8.75H4.7875L11.775 1.7625L10 0L0 10L10 20L11.7625 18.2375L4.7875 11.25H20V8.75Z" fill="#737373"/>
                        </svg>
                    </button>
                    <div className={styles.detailedViewHeaderContent}>
                        <h1 className={styles.detailedViewTitle}>Result History</h1>
                        <p className={styles.detailedViewSubtitle}>View your listing score analysis results</p>
                    </div>
                </div>
                <div className={styles.loadingContainer}>
                    <FaSpinner className="fa-spin" />
                    <p>Loading results...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.detailedViewHeader}>
                    <button 
                        className={styles.backButton}
                        onClick={() => navigate(-1)}
                        aria-label="Back"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 8.75H4.7875L11.775 1.7625L10 0L0 10L10 20L11.7625 18.2375L4.7875 11.25H20V8.75Z" fill="#737373"/>
                        </svg>
                    </button>
                    <div className={styles.detailedViewHeaderContent}>
                        <h1 className={styles.detailedViewTitle}>Result History</h1>
                        <p className={styles.detailedViewSubtitle}>View your listing score analysis results</p>
                    </div>
                </div>
                <div className={styles.errorMessage}>
                    <strong>Error:</strong> {error}
                    <button onClick={fetchListingScoreHistory} className={styles.retryButton}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // Show detailed view as full page
    if (showDetailedView && selectedScoreData) {
        return (
            <div className={styles.container}>
                <div className={styles.detailedViewHeader}>
                    <button 
                        className={styles.backButton}
                        onClick={handleCloseDetailedView}
                        aria-label="Back to results"
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 8.75H4.7875L11.775 1.7625L10 0L0 10L10 20L11.7625 18.2375L4.7875 11.25H20V8.75Z" fill="#737373"/>
                        </svg>

                    </button>
                    <div className={styles.detailedViewHeaderContent}>
                        <h1 className={styles.detailedViewTitle}>Listing Score Analysis</h1>
                        <p className={styles.detailedViewSubtitle}>Real-time evaluation based on Amazon best practices</p>
                    </div>
                </div>

                <div className={styles.detailedViewContent}>
                    {selectedScoreData.analysis && (
                        <>
                            <ResultsSection productData={selectedScoreData} />
                            
                            <section className={`${listingScoreStyles.section2} ${styles.sectionWithMargins}`}>
                                <ActionPlanSection 
                                    productData={{ analysis: selectedScoreData.analysis }} 
                                    showDetailedAnalysis={true}
                                />
                                <ComponentAnalysisSection productData={{ analysis: selectedScoreData.analysis }} />
                            </section>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.detailedViewHeader}>
                {selectedBatchId ? (
                    <>
                        <button
                            className={styles.backButton}
                            onClick={handleBackToBatches}
                            aria-label="Back to batches"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 8.75H4.7875L11.775 1.7625L10 0L0 10L10 20L11.7625 18.2375L4.7875 11.25H20V8.75Z" fill="#737373"/>
                            </svg>
                        </button>
                        <div className={styles.detailedViewHeaderContent}>
                            <h1 className={styles.detailedViewTitle}>Bulk Results</h1>
                            <p className={styles.detailedViewSubtitle}>{batchResults.length} ASIN{batchResults.length !== 1 ? 's' : ''} in this batch</p>
                        </div>
                    </>
                ) : (
                    <>
                        <button
                            className={styles.backButton}
                            onClick={() => navigate(-1)}
                            aria-label="Back"
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 8.75H4.7875L11.775 1.7625L10 0L0 10L10 20L11.7625 18.2375L4.7875 11.25H20V8.75Z" fill="#737373"/>
                            </svg>
                        </button>
                        <div className={styles.detailedViewHeaderContent}>
                            <h1 className={styles.detailedViewTitle}>Result History</h1>
                            <p className={styles.detailedViewSubtitle}>View your listing score analysis results</p>
                        </div>
                    </>
                )}
            </div>

            {selectedBatchId ? (
                batchResults.length > 0 ? (
                    <>
                        <ResultsGrid
                            results={batchResults}
                            onViewScore={handleViewScore}
                            currentPage={currentPage}
                            productsPerPage={PRODUCTS_PER_PAGE}
                            onUpgrade={handleUpgradeListing}
                        />
                        {batchTotalPages > 1 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={batchTotalPages}
                                onPageChange={handleBatchPageChange}
                            />
                        )}
                    </>
                ) : (
                    <div className={styles.emptyState}>
                        <h3>No results in this batch</h3>
                        <button type="button" onClick={handleBackToBatches} className={styles.retryButton}>Back to batches</button>
                    </div>
                )
            ) : hasBatches ? (
                <div className={styles.batchGrid}>
                    {batches.map((batch) => (
                        <BatchCard
                            key={batch.task_id}
                            batch={batch}
                            onClick={() => {
                                setSelectedBatchId(batch.task_id);
                                setCurrentPage(1);
                            }}
                        />
                    ))}
                </div>
            ) : (
                <div className={styles.emptyState}>
                    <h3>No results found</h3>
                    <p>Analyze some ASINs first to see results here.</p>
                </div>
            )}
        </div>
    );
};

export default ListingScoreResultHistory;
export { ResultCard, BatchCard, groupResultsByBatch };

